import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAuthGuard } from '@components/AuthGuard';
import {
  ROUTE_ABS_CONTENT,
  ROUTE_ABS_CONTENT_GUEST_ROOMS_LIST,
  ROUTE_ABS_CONTENT_MEETING_SPACES_LIST,
  ROUTE_ABS_CONTENT_PACKAGES_LIST,
  ROUTE_ABS_CONTENT_SERVICES_AUDIOVISUALS_LIST,
  ROUTE_ABS_CONTENT_SERVICES_FOOD_BEVERAGES_LIST,
  ROUTE_ABS_CONTENT_SERVICES_OTHERS_LIST,
  ROUTE_ABS_DOCUMENTS,
  ROUTE_ABS_DOCUMENTS_EDOCUMENTS_LIST,
  ROUTE_ABS_DOCUMENTS_MEDIA_LIST,
  ROUTE_ABS_DOCUMENTS_TEMPLATES_LIST,
  ROUTE_ABS_DOCUMENTS_TERMS_LIST,
  ROUTE_ABS_FINANCE,
  ROUTE_ABS_FINANCE_PAYMENTS_LIST,
  ROUTE_ABS_FINANCE_TAXES_LIST,
  ROUTE_ABS_GROUPER,
  ROUTE_ABS_GROUPER_LINKS,
  ROUTE_ABS_INTEGRATIONS,
  ROUTE_ABS_INTEGRATIONS_LIST,
  ROUTE_ABS_MAIL,
  ROUTE_ABS_MAIL_LIST,
  ROUTE_ABS_MANAGEMENT,
  ROUTE_ABS_MANAGEMENT_AUDIT_LOGS_LIST,
  ROUTE_ABS_MANAGEMENT_CANCEL_REASONS_LIST,
  ROUTE_ABS_MANAGEMENT_CHANNELS_LIST,
  ROUTE_ABS_MANAGEMENT_COMMERCIAL_ACTION_CHOICES_LIST,
  ROUTE_ABS_MANAGEMENT_INDUSTRY_TYPES_LIST,
  ROUTE_ABS_MANAGEMENT_ORDER_TASK_TYPES_LIST,
  ROUTE_ABS_MANAGEMENT_SEGMENTATION_TEAMS_LIST,
  ROUTE_ABS_MANAGEMENT_SOURCES_LIST,
  ROUTE_ABS_MANAGEMENT_SUBSEGMENTATIONS_LIST,
  ROUTE_ABS_MANAGEMENT_USERS_LIST,
  ROUTE_ABS_MCP,
  ROUTE_ABS_RESERVATIONS_LIST,
} from '@constants/routes';
import { TERMS_SCOPE_READ } from '@constants/scopes';
import useStoreAuth from '@hooks/useStoreAuth';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import CableIcon from '@mui/icons-material/Cable';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DrawIcon from '@mui/icons-material/Draw';
import EmailIcon from '@mui/icons-material/Email';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LanguageIcon from '@mui/icons-material/Language';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TuneIcon from '@mui/icons-material/Tune';
import { TokenAttributesScopesEnum } from '@spazious/client-backoffice';

import { DOCUMENTS_NAV_SECTION_ID } from './navTourAnchors.js';
import type { NavChild, NavLeaf, NavSection } from './types.js';

// Backend user.role enum — only the admin tier matters for nav gating today.
const SPAZIOUS_ADMIN_ROLE = 1;

const REPORTING_URL = '/reporting';
const REPORTING_ORDERS_URL = `${REPORTING_URL}/orders`;
const REPORTING_LEADS_URL = `${REPORTING_URL}/leads`;
const REPORTING_BUDGETS_URL = `${REPORTING_URL}/budgets`;
const REPORTING_DIAGRAMS_URL = `${REPORTING_URL}/diagramming`;

const SCHEDULERS_URL = '/schedulers';
const SCHEDULERS_ORDERS_URL = `${SCHEDULERS_URL}/orders`;
const SCHEDULERS_ROOM_URL = `${SCHEDULERS_URL}/rooms`;
const SCHEDULERS_ACTIVITIES_URL = `${SCHEDULERS_URL}/activities`;

const ORDERS_URL = '/reservations';
const ORDERS_LIST_URL = ROUTE_ABS_RESERVATIONS_LIST;
const ORDERS_LEADS_LIST_URL = `${ORDERS_URL}/leads`;
const ORDERS_PIPELINE_URL = `${ORDERS_URL}/pipeline`;
const ORDERS_TASKS_URL = `${ORDERS_URL}/tasks`;

const CRM_URL = '/crm';
const CRM_ACCOUNTS_URL = `${CRM_URL}/accounts`;
const CRM_CONTACTS_URL = `${CRM_URL}/contacts`;

const DIAGRAMMING_URL = '/diagramming';
const DIAGRAMMING_DIAGRAMS_URL = `${DIAGRAMMING_URL}/diagrams`;
const DIAGRAMMING_SETUP_TEMPLATE_URL = `${DIAGRAMMING_URL}/setup-template`;
const DIAGRAMMING_INVENTORY_URL = `${DIAGRAMMING_URL}/inventory`;

// `scope` is widened to plain strings because not every scope the backend serves
// is in the SDK enum yet (see `TERMS_SCOPE_READ`, SPAZ2-3259).
function leaf(label: string, href: string, scope: string | string[]): NavLeaf {
  return { kind: 'leaf', label, href, scope };
}

function useBuildNavConfig(): NavSection[] {
  const { hasScope } = useAuthGuard({ raiseError: false, scopes: [] });
  const [auth] = useStoreAuth();
  const intl = useIntl();
  const isSpaziousAdmin = auth?.role === SPAZIOUS_ADMIN_ROLE;

  return useMemo(() => {
    const sections: NavSection[] = [];

    const filter = (leaves: NavLeaf[]) => leaves.filter((l) => !l.scope || hasScope(l.scope));

    // Reporting
    const reportingChildren = filter([
      leaf('Orders', REPORTING_ORDERS_URL, TokenAttributesScopesEnum.ReadorderReporting),
      leaf('Leads', REPORTING_LEADS_URL, TokenAttributesScopesEnum.ReadleadsReporting),
      leaf(
        'Budgets',
        REPORTING_BUDGETS_URL,
        TokenAttributesScopesEnum.ReadhotelMonthlyBudgetReports
      ),
      leaf('Diagrams', REPORTING_DIAGRAMS_URL, TokenAttributesScopesEnum.ReaddiagrammingReporting),
    ]);
    if (reportingChildren.length > 0) {
      sections.push({
        id: 'reporting',
        label: 'Reporting',
        icon: <BarChartIcon />,
        href: REPORTING_URL,
        children: reportingChildren,
      });
    }

    // Schedulers
    const schedulersChildren = filter([
      leaf('Orders', SCHEDULERS_ORDERS_URL, TokenAttributesScopesEnum.ReadgenericOrders),
      leaf('Rooms', SCHEDULERS_ROOM_URL, TokenAttributesScopesEnum.ReadguestRooms),
      leaf('Activities', SCHEDULERS_ACTIVITIES_URL, [
        TokenAttributesScopesEnum.ReadmeetingRooms,
        TokenAttributesScopesEnum.ReadgenericOrderActivities,
        TokenAttributesScopesEnum.ReadmeetingRoomBlocks,
      ]),
    ]);
    if (schedulersChildren.length > 0) {
      sections.push({
        id: 'schedulers',
        label: 'Schedulers',
        icon: <CalendarMonthIcon />,
        href: SCHEDULERS_URL,
        children: schedulersChildren,
      });
    }

    // Orders / Reservations
    const ordersChildren = filter([
      leaf('List', ORDERS_LIST_URL, TokenAttributesScopesEnum.ReadgenericOrders),
      leaf('Leads', ORDERS_LEADS_LIST_URL, TokenAttributesScopesEnum.Readlead),
      leaf('Pipeline', ORDERS_PIPELINE_URL, TokenAttributesScopesEnum.ReadguestRooms),
      leaf('Tasks', ORDERS_TASKS_URL, [
        TokenAttributesScopesEnum.ReadmeetingRooms,
        TokenAttributesScopesEnum.ReadgenericOrderActivities,
      ]),
    ]);
    if (ordersChildren.length > 0) {
      sections.push({
        id: 'orders',
        label: 'Orders',
        icon: <ShoppingCartIcon />,
        href: ORDERS_URL,
        children: ordersChildren,
      });
    }

    // CRM
    const crmChildren = filter([
      leaf('Accounts', CRM_ACCOUNTS_URL, TokenAttributesScopesEnum.Readcrm),
      leaf('Contacts', CRM_CONTACTS_URL, TokenAttributesScopesEnum.Readcrm),
    ]);
    if (crmChildren.length > 0) {
      sections.push({
        id: 'crm',
        label: 'CRM',
        icon: <PeopleAltIcon />,
        href: CRM_URL,
        children: crmChildren,
      });
    }

    // Finance
    const financeLeaves = filter([
      leaf('Payments', ROUTE_ABS_FINANCE_PAYMENTS_LIST, TokenAttributesScopesEnum.Readpayments),
    ]);
    const financeGroups: NavChild[] = hasScope(TokenAttributesScopesEnum.ReadtaxHotelChains)
      ? [
          {
            kind: 'group',
            label: 'Taxes',
            icon: <ReceiptLongIcon fontSize="small" />,
            isActive: (pathname) => pathname.includes('/taxes/'),
            children: [
              leaf(
                'List',
                ROUTE_ABS_FINANCE_TAXES_LIST,
                TokenAttributesScopesEnum.ReadtaxHotelChains
              ),
            ],
          },
        ]
      : [];
    const financeChildren: NavChild[] = [...financeLeaves, ...financeGroups];
    if (financeChildren.length > 0) {
      sections.push({
        id: 'finance',
        label: 'Finance',
        icon: <AttachMoneyIcon />,
        href: ROUTE_ABS_FINANCE,
        children: financeChildren,
      });
    }

    // Content
    const contentChildren = filter([
      leaf(
        'Meeting Spaces',
        ROUTE_ABS_CONTENT_MEETING_SPACES_LIST,
        TokenAttributesScopesEnum.ReadmeetingRooms
      ),
      leaf(
        'Guest rooms',
        ROUTE_ABS_CONTENT_GUEST_ROOMS_LIST,
        TokenAttributesScopesEnum.ReadguestRooms
      ),
      leaf(
        'Food and Beverages',
        ROUTE_ABS_CONTENT_SERVICES_FOOD_BEVERAGES_LIST,
        TokenAttributesScopesEnum.Readservices
      ),
      leaf(
        'Audiovisuals',
        ROUTE_ABS_CONTENT_SERVICES_AUDIOVISUALS_LIST,
        TokenAttributesScopesEnum.Readservices
      ),
      leaf(
        'Extraservices',
        ROUTE_ABS_CONTENT_SERVICES_OTHERS_LIST,
        TokenAttributesScopesEnum.Readservices
      ),
      leaf('Packages', ROUTE_ABS_CONTENT_PACKAGES_LIST, TokenAttributesScopesEnum.Readpackages),
    ]);
    if (contentChildren.length > 0) {
      sections.push({
        id: 'content',
        label: 'Content',
        icon: <Inventory2Icon />,
        href: ROUTE_ABS_CONTENT,
        children: contentChildren,
      });
    }

    // Documents
    // Each entry follows its own read scope, the same rule the route gate applies.
    const documentsChildren = filter([
      leaf(
        'Templates',
        ROUTE_ABS_DOCUMENTS_TEMPLATES_LIST,
        TokenAttributesScopesEnum.Readtemplates
      ),
      leaf('Terms & Conditions', ROUTE_ABS_DOCUMENTS_TERMS_LIST, TERMS_SCOPE_READ),
      leaf('Media', ROUTE_ABS_DOCUMENTS_MEDIA_LIST, TokenAttributesScopesEnum.Readmedias),
      leaf(
        'eDocuments',
        ROUTE_ABS_DOCUMENTS_EDOCUMENTS_LIST,
        TokenAttributesScopesEnum.ReadorderDocuments
      ),
    ]);
    if (documentsChildren.length > 0) {
      sections.push({
        id: DOCUMENTS_NAV_SECTION_ID,
        label: 'Documents',
        icon: <FolderCopyIcon />,
        href: ROUTE_ABS_DOCUMENTS,
        children: documentsChildren,
      });
    }

    // Diagramming
    const diagrammingChildren = filter([
      leaf(
        'Diagrams',
        DIAGRAMMING_DIAGRAMS_URL,
        TokenAttributesScopesEnum.ReadvirtualMeetingRoomSetup
      ),
      leaf(
        'Setup Templates',
        DIAGRAMMING_SETUP_TEMPLATE_URL,
        TokenAttributesScopesEnum.ReadvirtualMeetingRoomSetupTemplate
      ),
      leaf('Inventory', DIAGRAMMING_INVENTORY_URL, TokenAttributesScopesEnum.ReadvirtualFurniture),
    ]);
    if (diagrammingChildren.length > 0) {
      sections.push({
        id: 'diagramming',
        label: 'Diagramming',
        icon: <DrawIcon />,
        href: DIAGRAMMING_URL,
        children: diagrammingChildren,
      });
    }

    // Management
    const managementChildren: NavChild[] = [];
    const usersLeaves = filter([
      leaf('List', ROUTE_ABS_MANAGEMENT_USERS_LIST, TokenAttributesScopesEnum.Readusers),
      leaf(
        'Segmentation Teams',
        ROUTE_ABS_MANAGEMENT_SEGMENTATION_TEAMS_LIST,
        TokenAttributesScopesEnum.ReadsegmentationTeams
      ),
    ]);
    if (usersLeaves.length > 0) {
      managementChildren.push({
        kind: 'group',
        label: 'Users',
        icon: <PersonIcon fontSize="small" />,
        isActive: (p) => p.includes('/users/') || p.includes('/segmentation-teams/'),
        children: usersLeaves,
      });
    }
    const choicesLeaves = filter([
      leaf(
        'Subsegmentations',
        ROUTE_ABS_MANAGEMENT_SUBSEGMENTATIONS_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
      leaf(
        'Channels',
        ROUTE_ABS_MANAGEMENT_CHANNELS_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
      leaf(
        'Sources',
        ROUTE_ABS_MANAGEMENT_SOURCES_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
      leaf(
        'Industry Types',
        ROUTE_ABS_MANAGEMENT_INDUSTRY_TYPES_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
      leaf(
        'Cancel Reasons',
        ROUTE_ABS_MANAGEMENT_CANCEL_REASONS_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
      leaf(
        'Order Task Types',
        ROUTE_ABS_MANAGEMENT_ORDER_TASK_TYPES_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
      leaf(
        'Commercial Actions',
        ROUTE_ABS_MANAGEMENT_COMMERCIAL_ACTION_CHOICES_LIST,
        TokenAttributesScopesEnum.ReadhotelChainChoices
      ),
    ]);
    if (choicesLeaves.length > 0) {
      managementChildren.push({
        kind: 'group',
        label: 'Choices',
        icon: <TuneIcon fontSize="small" />,
        isActive: (p) =>
          p.includes('/channels/') ||
          p.includes('/sources/') ||
          p.includes('/industry-types/') ||
          p.includes('/cancel-reasons/') ||
          p.includes('/subsegmentations/') ||
          p.includes('/order-task-types/') ||
          p.includes('/commercial-action-choices/'),
        children: choicesLeaves,
      });
    }
    const adminLeaves = filter([
      leaf(
        'Audit Log',
        ROUTE_ABS_MANAGEMENT_AUDIT_LOGS_LIST,
        TokenAttributesScopesEnum.ReadlogEntries
      ),
    ]);
    if (adminLeaves.length > 0) {
      managementChildren.push({
        kind: 'group',
        label: 'Admin',
        icon: <AdminPanelSettingsIcon fontSize="small" />,
        isActive: (p) => p.includes('/audit-logs/'),
        children: adminLeaves,
      });
    }
    if (managementChildren.length > 0) {
      sections.push({
        id: 'management',
        label: 'Management',
        icon: <ManageAccountsIcon />,
        href: ROUTE_ABS_MANAGEMENT,
        children: managementChildren,
      });
    }

    // Grouper
    const grouperChildren = isSpaziousAdmin
      ? filter([leaf('Links', ROUTE_ABS_GROUPER_LINKS, TokenAttributesScopesEnum.Readgrouper)])
      : [];
    if (grouperChildren.length > 0) {
      sections.push({
        id: 'grouper',
        label: 'Grouper',
        icon: <LanguageIcon />,
        href: ROUTE_ABS_GROUPER,
        children: grouperChildren,
      });
    }

    // Integrations
    const integrationsChildren: NavLeaf[] = [
      ...filter([
        leaf('List', ROUTE_ABS_INTEGRATIONS_LIST, TokenAttributesScopesEnum.Readintegrations),
      ]),
      {
        kind: 'leaf',
        label: intl.formatMessage({ id: 'MCP.NAVIGATION.LABEL', defaultMessage: 'MCP' }),
        href: ROUTE_ABS_MCP,
      },
    ];
    sections.push({
      id: 'integrations',
      label: 'Integrations',
      icon: <CableIcon />,
      href: ROUTE_ABS_INTEGRATIONS,
      children: integrationsChildren,
      isActive: (pathname) =>
        pathname.startsWith(ROUTE_ABS_INTEGRATIONS) ||
        pathname === ROUTE_ABS_MCP ||
        pathname.startsWith(`${ROUTE_ABS_MCP}/`),
    });

    // Email
    const mailChildren = isSpaziousAdmin
      ? filter([leaf('List', ROUTE_ABS_MAIL_LIST, TokenAttributesScopesEnum.Reademails)])
      : [];
    if (mailChildren.length > 0) {
      sections.push({
        id: 'mail',
        label: 'Mail',
        icon: <EmailIcon />,
        href: ROUTE_ABS_MAIL,
        children: mailChildren,
      });
    }

    return sections;
  }, [hasScope, intl, isSpaziousAdmin]);
}

const NavConfigContext = createContext<NavSection[] | null>(null);

export function NavConfigProvider({ children }: { children: ReactNode }) {
  const sections = useBuildNavConfig();
  return <NavConfigContext.Provider value={sections}>{children}</NavConfigContext.Provider>;
}

export function useNavConfig(): NavSection[] {
  const ctx = useContext(NavConfigContext);
  if (!ctx) throw new Error('useNavConfig must be used within a NavConfigProvider');
  return ctx;
}
